// Documento: Configuración de WhatsApp Cloud
// Categoría: WhatsApp
// Última actualización: 2024-12-28

const document = {
  "id": "configuracion-whatsapp-cloud",
  "title": "Configuración de WhatsApp Cloud",
  "category": "WhatsApp",
  "description": "Guía completa para configurar WhatsApp Cloud API desde cero. Aprende a crear una aplicación de Facebook, configurar webhooks, agregar números de teléfono y gestionar plantillas de mensajes.",
  "explanations": [
    {
      "title": "Consideraciones importantes antes de comenzar",
      "content": "Antes de configurar WhatsApp Cloud, tenga en cuenta estas limitaciones importantes:\n\n• Si desea utilizar un número que ya está en uso en la aplicación comercial o de cliente de WhatsApp, tendrá que eliminar su cuenta en la aplicación comercial o de cliente de WhatsApp.\n\n• El número de teléfono utilizado para la automatización de WhatsApp no se puede usar en la aplicación comercial o de cliente de WhatsApp.\n\n• Aún puedes usar el número para fines cotidianos, como llamar y recibir mensajes, después de registrarlo en la plataforma WhatsApp Business.\n\n• Una vez que uses un teléfono para la automatización de WhatsApp, ya no podrás usar ese número en la aplicación WhatsApp Business.\n\n• No necesitas un número de teléfono real para realizar pruebas. Facebook te proporcionará un número de teléfono gratuito para realizar pruebas."
    },
    {
      "title": "¿Cómo crear mensajes de plantilla de WhatsApp?",
      "content": "Las plantillas de mensajes de WhatsApp permiten a las empresas utilizar plantillas creadas y aprobadas previamente para enviar mensajes estructurados a los clientes que hayan optado por recibir notificaciones. Los mensajes pueden incluir recordatorios de citas, información de envío, resolución de problemas o actualizaciones de pagos.\n\nDespués de que Facebook haya aprobado tu mensaje de plantilla, ve a Runamatic > Configuración > Canales > WhatsApp y sincroniza las plantillas de WhatsApp nuevamente."
    },
    {
      "title": "Precios de Runamatic",
      "content": "Runamatic no cobra ningún cargo adicional por enviar y recibir cualquier tipo de mensaje a través del canal de WhatsApp. Tu empresa puede usar el canal de WhatsApp con nuestro plan gratuito."
    },
    {
      "title": "Precios de la plataforma WhatsApp Business",
      "content": "La plataforma WhatsApp Business no es gratuita. La plataforma WhatsApp Business utiliza un modelo de precios basado en conversaciones. Cuando tu empresa envía mensajes a los clientes a través de la plataforma WhatsApp Business, se te cobrará por conversación.\n\nPara la mayoría de las pequeñas empresas, WhatsApp Automation será gratuita porque cada mes las primeras 1000 conversaciones son GRATUITAS. Las empresas solo comienzan a pagar si tienen más de 1000 conversaciones por mes."
    },
    {
      "title": "Tipos de conversaciones",
      "content": "Con el modelo de precios basado en conversaciones, las conversaciones de la plataforma WhatsApp Business se dividen en dos categorías:\n\n**Iniciada por el usuario:** conversación que se inicia en respuesta a un mensaje de un usuario. Siempre que una empresa responda a un usuario dentro del período de servicio al cliente de 24 horas, ese mensaje se asociará con una conversación iniciada por el usuario. Las empresas pueden enviar mensajes de formato libre dentro de este período de servicio al cliente de 24 horas.\n\n**Iniciada por una empresa:** conversación que se inicia cuando una empresa envía un mensaje a un usuario fuera del horario de atención al cliente de 24 horas. Los mensajes que inician una conversación iniciada por una empresa requerirán una plantilla de mensaje.\n\nTodas las conversaciones se miden en sesiones fijas de 24 horas. Una conversación comienza cuando se envía el primer mensaje comercial de una conversación, ya sea iniciado por la empresa o en respuesta a un mensaje de usuario, y finaliza 24 horas después."
    },
    {
      "title": "Conversaciones de nivel gratuito",
      "content": "Las primeras 1000 conversaciones de cada mes son gratuitas. De esta manera, tu empresa puede crear experiencias que a tus clientes les encantarán antes de tener que pagar. Cada cuenta de WhatsApp Business (WABA) recibirá 1000 conversaciones gratuitas al mes. Estas conversaciones pueden ser iniciadas por el usuario o por la empresa.\n\nIncluso si una WABA tiene varios números asociados, el umbral del nivel gratuito sigue siendo 1000, ya que el nivel gratuito se proporciona en el nivel WABA. El nivel gratuito se actualiza mensualmente, según la zona horaria asociada con la WABA."
    },
    {
      "title": "Conversaciones de puntos de entrada gratuitos",
      "content": "Las conversaciones no se cobran cuando los usuarios envían mensajes a empresas mediante botones de llamada a la acción en anuncios que hacen clic en WhatsApp o en botones de llamada a la acción de una página de Facebook. Las conversaciones de punto de entrada gratuito solo pueden ser iniciadas por el usuario.\n\nLa primera conversación que comienza desde el punto de entrada es gratuita, luego se cobran las conversaciones posteriores con el usuario. Los anuncios que hacen clic en WhatsApp tienen un precio estándar. La conversación que se inicia a partir del anuncio es gratuita, pero no el anuncio en sí."
    },
    {
      "title": "Información sobre tarifas",
      "content": "Las tarifas de las conversaciones iniciadas por empresas y por usuarios varían según el mercado (país o región). Los cargos por conversaciones se basan en el código de país del usuario (destinatario). Puede consultar los detalles de precios en la documentación oficial de Facebook para desarrolladores.\n\nTus primeras 1000 conversaciones al mes son gratuitas. Si quieres superar el nivel gratuito de 1000 conversaciones al mes, debes agregar un método de pago a tu cuenta de WhatsApp Business en Business Manager."
    }
  ],
  "videoTutorial": {
    "title": "Video Tutorial",
    "url": "https://youtu.be/RDxshqXV_T8",
    "description": "Mira este video tutorial paso a paso para configurar WhatsApp Cloud API desde cero"
  },
  "steps": [
    {
      "title": "Registrarse como Meta Developer",
      "content": "Primero debe registrarse como desarrollador de Meta y habilitar la autenticación de dos factores para su cuenta."
    },
    {
      "title": "Crear una aplicación de Facebook",
      "content": "Vaya a la lista de aplicaciones de Facebook y haga clic en \"Crear app\"."
    },
    {
      "title": "Seleccionar portfolio comercial",
      "content": "Seleccione su portfolio comercial y haga clic en \"Siguiente\"."
    },
    {
      "title": "Configurar tipo de aplicación",
      "content": "Seleccione \"Otro\" y haga clic en \"Siguiente\". Luego seleccione \"Negocios\" y haga clic en \"Siguiente\"."
    },
    {
      "title": "Completar información de la aplicación",
      "content": "Póngale nombre a su app (no será visible a los clientes). Elija su correo de contacto personal. Seleccione su portfolio comercial. Haga clic en \"Crear App\"."
    },
    {
      "title": "Configurar WhatsApp",
      "content": "Una vez creada la aplicación, haga clic en \"Configurar WhatsApp\"."
    },
    {
      "title": "Acceder a configuración de API",
      "content": "Acceda a \"Configuración de la API\"."
    },
    {
      "title": "Configurar webhooks",
      "content": "Haga clic en \"Configurar webhooks\". En URL de devolución de llamada ponga: https://app.runamatic.io/webhooks/whatsapp.php. En Token de verificación ponga: whatsapp"
    },
    {
      "title": "Suscribirse a campos",
      "content": "Subscríbase a todos los campos disponibles (si algunos de estos dan error, ignórelo y continúe con los demás)."
    },
    {
      "title": "Agregar número de teléfono",
      "content": "Acceda nuevamente a \"Configuración de la API\" y haga clic en \"Agregar número de teléfono\"."
    },
    {
      "title": "Seguir instrucciones de verificación",
      "content": "Siga las instrucciones para agregar su número de teléfono. Para finalizar se le enviará un SMS con un código que autentica la línea telefónica."
    },
    {
      "title": "Configurar política de privacidad",
      "content": "Acceda a \"Configuración de la app > Básica\". Agregue la URL de política de privacidad: https://runamatic.io/privacy-policy. Ponga el modo de la app en \"Activo\"."
    },
    {
      "title": "Generar token de acceso",
      "content": "Vaya a \"Configuración de la API\" y haga clic en \"Generar token de acceso\"."
    },
    {
      "title": "Agregar método de pago",
      "content": "Agregue un método de pago. Esto es un mecanismo de Meta para validar cuentas, no incluirá ningún gasto adicional dentro de los 1000 mensajes mensuales. Para agregar un método de pago acceda a \"Configuración de la API > Agregar método de pago\"."
    },
    {
      "title": "Completar configuración de pago",
      "content": "En la nueva página de Meta haga clic en \"Configuración de pago\" y complete la información solicitada."
    },
    {
      "title": "Configurar perfil del número",
      "content": "Vuelva a aplicaciones de Facebook. Acceda a \"Configuración\" y haga clic en \"Administrar números de teléfono\". Haga clic en el nombre arriba del número de teléfono que acaba de agregar. En esta sección puede configurar la foto de perfil y todos los detalles visibles para sus clientes."
    },
    {
      "title": "Conectar con Runamatic",
      "content": "Diríjase a Runamatic. Acceda a Ajustes > Canales > WhatsApp > Conectar. Elija su cuenta y su número telefónico."
    },
    {
      "title": "Configurar permisos en cuenta personal",
      "content": "Para evitar problemas de permisos acceda a la cuenta personal de Facebook que controla el portfolio comercial. Acceda a \"Configuración y Privacidad > Configuración\" y haga clic en \"Integraciones comerciales\"."
    },
    {
      "title": "Otorgar permisos a Appcontx",
      "content": "Haga clic en \"Ver y editar\" sobre la aplicación Appcontx. Dele todos los permisos que estén listados y haga clic en \"Guardar\"."
    },
    {
      "title": "Activar cuenta de WhatsApp",
      "content": "Asegúrese de activar sus cuentas de WhatsApp. Su WhatsApp fue conectado con éxito a nuestra plataforma."
    }
  ],
  "tags": [
    "whatsapp",
    "cloud",
    "api",
    "facebook",
    "meta",
    "developer",
    "webhooks",
    "plantillas",
    "precios",
    "configuración"
  ],
  "lastUpdated": "2024-12-28",
  "difficulty": "intermedio"
};

export default document;
